import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { InlineRowGrid, Modal, type InlineColumn } from "@components/input";
import { CatalogPicker } from "@components/catalog";
import { useUnits } from "@units/useUnits";
import { fmtNum } from "@lib/format";
import { useCatalogLabels } from "@hooks/useCatalogLabels";
import {
  useGetReportActivitiesQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
} from "@api/pApi";
import type { ActivityOut, TimeClass } from "@api/types";

const TIME_CLASSES: TimeClass[] = ["productive", "npt", "flat_waiting"];

// Distinct phase_entry_key values present in the demo data (sorted, deduped) —
// the phase field is a soft catalog ref the API doesn't validate, so a curated
// dropdown of the real values is friendlier than free text. The row's own value
// is always kept selectable even if it's not in this list.
const PHASE_OPTIONS = [
  "ARSU", "CALI", "DTM", "DTM-DTM", "DTM-POST", "DTM-PRE", "G-CEMENT", "G-ENTUBA",
  "G-PERF", "G-SACA", "I2-ARMA", "I2-CABEZA", "I2-CEMENT", "I2-ENTUBA", "I2-PERF",
  "I2-SACA", "I-ARMA", "I-CABEZA", "I-CEMENT", "I-ENTUBA", "I-PERF", "I-SACA",
  "P-ARMA", "P-CEMENT", "P-ENTUBA", "POSTFCT-CALI", "POSTFCT-DTM", "POSTFCT-SK",
  "P-PERF", "PREFCT-DTM", "PREFCT-PUN", "PREFCT-SK", "PREFPB-DTM", "PREFPB-PRUEB",
  "PREFPB-SK", "P-SACA", "SK G-G", "SK G-I1", "SK I1-I1", "SK I1-I2", "SK OTROS",
  "SK P-I2", "STIM-DTM", "STIM-FRAC", "STIM-SETUP", "WL-PDWN",
];

// Distinct activity_code_entry_key values in the demo data (the activity_codes
// catalog values in actual use), sorted + deduped — a native dropdown (matching
// phase) reads cleaner than the search-popover for the demo. Row value kept.
const ACTIVITY_OPTIONS = [
  "ACOND", "ADMISION", "APEINE", "ARMA", "ARSU", "ATORRE", "AUDEQ", "BADAPT",
  "BAJA", "BAJARSS", "BHA", "BINI", "BOLA", "BOP", "BUILDUP", "BULLHEADING",
  "CABEZA", "CALIBRA", "CEMENT", "CIRC", "CIRCULA", "CLIM", "CONTROL",
  "CONVERSION", "CSGMANIO", "CTPUN", "CTRL", "CUELGA", "DARMA", "DBEARING",
  "DBHA", "DBOP", "DCEMENT", "DCRT", "DENTUB", "DESENTUBA", "DESMONTACT",
  "DESMONTAPB", "DESMONTASET", "DGYRO", "DISOLUCION", "DMPD", "DOBLEPIN", "DRAM",
  "DTM", "DWB", "ENTREGA", "ENTUBA", "ESLU", "FCHECK", "FINGERPRINT", "FIT",
  "FRAC", "GYRO", "LAVA", "LIMP", "LUBMEC", "MANEQU", "MANIOBRA", "MBEARING",
  "MBOP", "MCEMENT", "MCRT", "MENTUB", "MLATERAL", "MLLAVE", "MMPD", "MONTACT",
  "MONTAPB", "MONTASET", "MRAM", "MTORRE", "MWB", "PBHA", "PDWN", "PERDIR",
  "PERHOR", "PERMDF", "PERRSS", "PERVER", "PESCA", "PILDORA", "PREFPB-SK",
  "PRESKI", "PRUEBA", "PRUEBABOP", "PRUEBHID", "RAMBOP", "RAMP", "RNON", "ROTA",
  "SACA", "SACAMDF", "SACR", "SETUPF", "SETUPW", "SIMULACRO", "SK-CT",
  "SK/DARMA", "SKIDDING", "TESTEO", "VAINA", "VEQ", "WLSUP", "WPUN", "WSLI",
];

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Clock-diff in hours from two "HH:MM" strings, midnight-wrap aware: if HASTA
 * is at or before DESDE the segment is treated as crossing midnight (+24 h).
 * Returns null unless BOTH times are well-formed "HH:MM". This is the single
 * source of truth for the derived HORAS cell and the stored duration_hours.
 */
const clockDiffHours = (from: string, to: string): number | null => {
  const m = (s: string): number | null => {
    const mm = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
    if (!mm) return null;
    const h = Number(mm[1]);
    const min = Number(mm[2]);
    if (h < 0 || h > 23 || min < 0 || min > 59) return null;
    return h * 60 + min;
  };
  const f = m(from);
  const tt = m(to);
  if (f == null || tt == null) return null;
  let diff = tt - f;
  if (diff <= 0) diff += 24 * 60;
  return diff / 60;
};

/** ISO (UTC) stored datetime → browser-local "HH:MM" for the time inputs. */
const isoToHHMM = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** Draft row for a new time-log activity (all string-typed inputs). */
interface ActivityDraft {
  phase_entry_key: string | null;
  activity_code_entry_key: string | null;
  // Clock times as browser-local "HH:MM"; HORAS is DERIVED from these (read-only).
  time_from: string;
  time_to: string;
  md_from: string;
  md_to: string;
  time_class: TimeClass;
  npt_class_key: string | null;
  activity_memo: string;
}

const emptyDraft = (): ActivityDraft => ({
  phase_entry_key: null,
  activity_code_entry_key: null,
  time_from: "",
  time_to: "",
  md_from: "",
  md_to: "",
  time_class: "productive",
  npt_class_key: null,
  activity_memo: "",
});

/** Load a served row's values back into an editable draft (inverse of toRow). */
const rowToDraft = (r: ActivityOut): ActivityDraft => ({
  phase_entry_key: r.phase_entry_key,
  activity_code_entry_key: r.activity_code_entry_key,
  time_from: isoToHHMM(r.time_from),
  time_to: isoToHHMM(r.time_to),
  md_from: r.md_from != null ? String(r.md_from) : "",
  md_to: r.md_to != null ? String(r.md_to) : "",
  time_class: r.time_class ?? "productive",
  npt_class_key: r.npt_class_entry_key,
  activity_memo: r.activity_memo ?? "",
});

/**
 * Build an ISO-8601 UTC datetime from the report date (YYYY-MM-DD) + a browser-
 * local "HH:MM". The `new Date("<date>T<hh:mm>:00")` form parses in the browser's
 * LOCAL timezone, and `.toISOString()` yields UTC — the TZ principle for the whole
 * app (enter/display in browser TZ, store UTC). If HASTA ≤ DESDE the segment wraps
 * past midnight, so HASTA lands on the next day. Returns null if date/time blank.
 */
const buildIso = (reportDate: string | undefined, hhmm: string, nextDay: boolean): string | null => {
  if (!reportDate || !hhmm.trim()) return null;
  const d = new Date(`${reportDate}T${hhmm}:00`);
  if (Number.isNaN(d.getTime())) return null;
  if (nextDay) d.setDate(d.getDate() + 1);
  return d.toISOString();
};

/** A draft → the ActivityCreate write body (shared by create + edit). */
const draftToBody = (d: ActivityDraft, reportDate?: string) => {
  const isNpt = d.time_class === "npt";
  const n = (s: string) => (s.trim() ? Number(s) : null);
  const diff = clockDiffHours(d.time_from, d.time_to);
  // HASTA wraps to the next day when it's at/before DESDE (midnight crossing).
  const fromMin = (() => {
    const mm = /^(\d{1,2}):(\d{2})$/.exec(d.time_from.trim());
    return mm ? Number(mm[1]) * 60 + Number(mm[2]) : null;
  })();
  const toMin = (() => {
    const mm = /^(\d{1,2}):(\d{2})$/.exec(d.time_to.trim());
    return mm ? Number(mm[1]) * 60 + Number(mm[2]) : null;
  })();
  const nextDay = fromMin != null && toMin != null && toMin <= fromMin;
  return {
    phase_entry_key: d.phase_entry_key,
    activity_code_entry_key: d.activity_code_entry_key,
    time_from: buildIso(reportDate, d.time_from, false),
    time_to: buildIso(reportDate, d.time_to, nextDay),
    duration_hours: diff != null ? Math.round(diff * 100) / 100 : null,
    md_from: n(d.md_from),
    md_to: n(d.md_to),
    time_class: d.time_class,
    npt_level: isNpt ? "1" : "0",
    npt_class_entry_key: isNpt ? d.npt_class_key : null,
    activity_memo: d.activity_memo || null,
  };
};

/**
 * Time-activity ENTRY grid (§ time-entry) — the operational time log for a daily
 * report: an IADC-style row per time segment (phase · activity code · DESDE/HASTA
 * clock window · derived duration · MD window · time-class · NPT classification ·
 * memo). The bottom row IS the add mechanism (shared InlineRowGrid); rows
 * edit/delete inline. HORAS is DERIVED from DESDE/HASTA (clock diff, midnight-wrap
 * aware) — never free-typed — so the ARCH "Detalle por Horas" formula
 * `=(HASTA−DESDE)*24` has the per-op start/end times it needs. TOTAL HOURS sums the
 * entered segments; NPT hours are called out. MD renders through the report's unit
 * profile (ft, 1-decimal). Writes go to Oxbo-P; the FE never recomputes a KPI.
 *
 * NPT is modeled as `time_class` (productive/npt/flat_waiting), NOT a boolean —
 * `npt` IS the flag; when npt the segment carries the npt_* classification keys.
 */
export function TimeEntryPanel({
  reportId,
  eventId,
  reportDate,
  unitProfileKey,
  readOnly = false,
}: {
  reportId: string;
  eventId: string;
  reportDate?: string;
  unitProfileKey?: string | null;
  readOnly?: boolean;
}) {
  const { t, locale } = useLocale();
  const { convertField, fmt } = useUnits(unitProfileKey);

  const { data } = useGetReportActivitiesQuery(reportId, { skip: !reportId });
  const [createActivity] = useCreateActivityMutation();
  const [updateActivity] = useUpdateActivityMutation();
  const [deleteActivity] = useDeleteActivityMutation();

  // Stable reference for the served rows — `data ?? []` would be a new array each
  // render and make the reset-effect below loop forever. Memoize on `data`.
  const activities = useMemo(() => data ?? [], [data]);

  // Optimistic local additions layered over served rows until the write lands.
  const [added, setAdded] = useState<ActivityOut[]>([]);
  useEffect(() => setAdded([]), [activities]);
  const rows = useMemo(() => [...activities, ...added], [activities, added]);

  // Ref to the panel root so the header "Agregar" action can jump to + focus the
  // inline entry row. Rubén could not find how to add a line because the add
  // control sits at the BOTTOM of a long table; this header button is always
  // visible and scrolls the entry row into view (FB — Rubén 2026-10-13).
  const rootRef = useRef<HTMLElement>(null);
  const focusAddRow = () => {
    const row = rootRef.current?.querySelector("tr.newline");
    if (!row) return;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    (
      row.querySelector("input, select, textarea, [contenteditable='true']") as HTMLElement | null
    )?.focus();
  };

  // Row being edited in the modal (null = closed) + its working draft.
  const [editing, setEditing] = useState<ActivityOut | null>(null);
  const [editDraft, setEditDraft] = useState<ActivityDraft>(emptyDraft);
  const openEdit = (r: ActivityOut) => {
    setEditing(r);
    // Load md as DISPLAY strings (converted + 1dp) so the edit inputs are clean.
    setEditDraft({ ...rowToDraft(r), md_from: mdToDisplayStr(r.md_from), md_to: mdToDisplayStr(r.md_to) });
  };
  const patchEdit = (p: Partial<ActivityDraft>) => setEditDraft((d) => ({ ...d, ...p }));
  const saveEdit = () => {
    if (!editing) return;
    // draftToBody carries every field except md; md is display→stored here.
    const body = { ...draftToBody(editDraft, reportDate), md_from: mdToStored(editDraft.md_from), md_to: mdToStored(editDraft.md_to) };
    // Optimistic local patch for the row (server row or a still-local add).
    setAdded((a) => a.map((r) => (r.entity_id === editing.entity_id ? { ...r, ...body } : r)));
    // Server rows go through the versioned PATCH, keyed off the STABLE entity_id.
    if (!editing.entity_id.startsWith("act-local-")) {
      void updateActivity({ entityId: editing.entity_id, reportId, body });
    }
    setEditing(null);
  };

  const depthUnit = convertField("daily_report", "md_current", 0, unitProfileKey).unit || "ft";
  const depthHeader = (base: string) => `${base} (${depthUnit})`;

  // MD round-trip helpers. Edit inputs show the DISPLAY value (converted + rounded
  // to 1 dp) so Ramon never sees a raw float like 4583.333276300001; on save we
  // convert DISPLAY → STORED so the persisted md is correct with no drift. Depth
  // conversion is linear with no offset, so the factor = display-per-stored-unit
  // derived from a reference value (1 on the demo's feet-passthrough, so a no-op).
  const mdFactor = (() => {
    const ref = convertField("daily_report", "md_current", 1000, unitProfileKey).value;
    return ref != null && ref !== 0 ? ref / 1000 : 1;
  })();
  const mdToDisplayStr = (stored: number | null): string => {
    if (stored == null) return "";
    const disp = convertField("daily_report", "md_current", stored, unitProfileKey).value ?? stored;
    return String(Math.round(disp * 10) / 10); // 1 dp, no trailing float noise
  };
  const mdToStored = (displayStr: string): number | null => {
    if (!displayStr.trim()) return null;
    const disp = Number(displayStr);
    if (!Number.isFinite(disp)) return null;
    return Math.round((disp / mdFactor) * 1000) / 1000; // back to stored units
  };

  // TOTAL HOURS — the day's logged time; NPT hours called out separately.
  const totalHours = rows.reduce((s, a) => s + (a.duration_hours ?? 0), 0);
  const nptHours = rows
    .filter((a) => a.time_class === "npt")
    .reduce((s, a) => s + (a.duration_hours ?? 0), 0);

  const depthCell = (v: number | null) =>
    fmt(convertField("daily_report", "md_current", v, unitProfileKey), { maximumFractionDigits: 1 });

  const classLabel = (c: TimeClass) => t(`time.class.${c}`);
  const nptLabel = useCatalogLabels("npt_classes");
  const phaseLabel = useCatalogLabels("phases");
  const actLabel = useCatalogLabels("activity_codes");

  const columns: InlineColumn<ActivityOut, ActivityDraft>[] = [
    {
      // Phase is a soft catalog ref (API doesn't validate) — a dropdown of the
      // real distinct values is friendlier than free text; the row's own value
      // stays selectable even if it's not in PHASE_OPTIONS.
      key: "phase",
      header: t("time.col.phase"),
      render: (r) => phaseLabel(r.phase_entry_key) || "—",
      input: (d, patch) => {
        const cur = d.phase_entry_key ?? "";
        const opts = cur && !PHASE_OPTIONS.includes(cur) ? [cur, ...PHASE_OPTIONS] : PHASE_OPTIONS;
        return (
          <select
            aria-label={t("time.col.phase")}
            value={cur}
            onChange={(e) => patch({ phase_entry_key: e.target.value || null })}
          >
            <option value="">{t("time.col.phase")}</option>
            {opts.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      // Activity is a dropdown of the activity_codes values in use (native select,
      // matching Phase); the row's own value stays selectable if off-list.
      key: "activity",
      header: t("time.col.activity"),
      width: 200,
      render: (r) => actLabel(r.activity_code_entry_key) || "—",
      input: (d, patch) => {
        const cur = d.activity_code_entry_key ?? "";
        const opts =
          cur && !ACTIVITY_OPTIONS.includes(cur) ? [cur, ...ACTIVITY_OPTIONS] : ACTIVITY_OPTIONS;
        return (
          <select
            aria-label={t("time.col.activity")}
            value={cur}
            onChange={(e) => patch({ activity_code_entry_key: e.target.value || null })}
          >
            <option value="">{t("time.col.activity")}</option>
            {opts.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      // DESDE — segment start clock time (browser-local HH:MM). Stored UTC; the
      // ARCH report derives HORAS as (HASTA−DESDE).
      key: "time_from",
      header: t("time.col.from"),
      render: (r) => isoToHHMM(r.time_from) || "—",
      input: (d, patch) => (
        <input
          type="time"
          aria-label={t("time.col.from")}
          value={d.time_from}
          onChange={(e) => patch({ time_from: e.target.value })}
        />
      ),
    },
    {
      // HASTA — segment end clock time (browser-local HH:MM). If ≤ DESDE the
      // segment is treated as crossing midnight (stored on the next day).
      key: "time_to",
      header: t("time.col.to"),
      render: (r) => isoToHHMM(r.time_to) || "—",
      input: (d, patch) => (
        <input
          type="time"
          aria-label={t("time.col.to")}
          value={d.time_to}
          onChange={(e) => patch({ time_to: e.target.value })}
        />
      ),
    },
    {
      // HORAS is DERIVED (read-only) from DESDE/HASTA — a live clock diff, never a
      // free-typed value — so the ARCH per-op start/end + duration stay consistent.
      key: "duration_hours",
      header: t("time.col.hours"),
      num: true,
      render: (r) =>
        r.duration_hours != null
          ? fmtNum(r.duration_hours, locale, { maximumFractionDigits: 2 })
          : "—",
      input: (d) => {
        const diff = clockDiffHours(d.time_from, d.time_to);
        return (
          <span className="mono tab-nums dim" aria-label={t("time.col.hours")}>
            {diff != null ? fmtNum(Math.round(diff * 100) / 100, locale, { maximumFractionDigits: 2 }) : "—"}
          </span>
        );
      },
    },
    {
      key: "md_from",
      header: depthHeader(t("time.col.depthFrom")),
      num: true,
      render: (r) => depthCell(r.md_from),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          aria-label={depthHeader(t("time.col.depthFrom"))}
          value={d.md_from}
          onChange={(e) => patch({ md_from: e.target.value })}
        />
      ),
    },
    {
      key: "md_to",
      header: depthHeader(t("time.col.depthTo")),
      num: true,
      render: (r) => depthCell(r.md_to),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          aria-label={depthHeader(t("time.col.depthTo"))}
          value={d.md_to}
          onChange={(e) => patch({ md_to: e.target.value })}
        />
      ),
    },
    {
      key: "time_class",
      header: t("time.col.class"),
      render: (r) =>
        r.time_class === "npt" ? (
          <span className="badge var-bad" title={r.npt_class_entry_key ?? undefined}>
            {classLabel("npt")} {r.npt_class_entry_key ? `· ${nptLabel(r.npt_class_entry_key)}` : ""}
          </span>
        ) : r.time_class === "flat_waiting" ? (
          <span className="badge">{classLabel("flat_waiting")}</span>
        ) : (
          <span className="dim">{classLabel("productive")}</span>
        ),
      input: (d, patch) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <select
            aria-label={t("time.col.class")}
            value={d.time_class}
            onChange={(e) => {
              const time_class = e.target.value as TimeClass;
              patch({
                time_class,
                npt_class_key: time_class === "npt" ? d.npt_class_key : null,
              });
            }}
          >
            {TIME_CLASSES.map((c) => (
              <option key={c} value={c}>
                {classLabel(c)}
              </option>
            ))}
          </select>
          {d.time_class === "npt" ? (
            <CatalogPicker
              catalogKey="npt_codes"
              value={d.npt_class_key}
              asOf={reportDate}
              compact
              serverSearch
              placeholder={t("time.col.nptClass")}
              ariaLabel={t("time.col.nptClass")}
              onChange={(code) => patch({ npt_class_key: code })}
            />
          ) : null}
        </div>
      ),
    },
    {
      // Memos are full sentences — bound the list column and clamp to 2 lines
      // with ellipsis (full text on hover via title) so a long memo can't stretch
      // the table. The edit modal shows the whole memo in a textarea.
      key: "activity_memo",
      header: t("time.col.description"),
      width: 260,
      render: (r) =>
        r.activity_memo ? (
          <span className="time-memo-cell" title={r.activity_memo}>
            {r.activity_memo}
          </span>
        ) : (
          "—"
        ),
      input: (d, patch) => (
        <input
          type="text"
          aria-label={t("time.col.description")}
          value={d.activity_memo}
          onChange={(e) => patch({ activity_memo: e.target.value })}
          placeholder={t("time.col.description")}
        />
      ),
    },
    // Per-row EDIT affordance — opens the row in a modal editor and PATCHes by
    // entity_id. Self-contained (no InlineRowGrid change); the entry row shows no
    // control (nothing to edit yet). Hidden when the report is read-only.
    {
      key: "__edit",
      header: "",
      width: 44,
      render: (r) =>
        readOnly ? null : (
          <button
            type="button"
            className="row-edit"
            aria-label={t("time.action.edit")}
            title={t("time.action.edit")}
            onClick={() => openEdit(r)}
          >
            <span aria-hidden="true">✎</span>
          </button>
        ),
    },
  ];

  const toRow = (d: ActivityDraft): ActivityOut | null => {
    // Require an activity code + both clock times — the anchor of a segment; HORAS
    // is derived from DESDE/HASTA (clock diff, midnight-wrap aware).
    const hrs = clockDiffHours(d.time_from, d.time_to);
    if (!d.activity_code_entry_key || hrs == null) return null;
    const body = draftToBody(d, reportDate);
    const isNpt = d.time_class === "npt";
    const localId = `act-local-${Date.now()}`;
    return {
      id: localId,
      entity_id: localId,
      version: null,
      status: "active",
      daily_report_id: reportId,
      event_id: eventId,
      well_uwi: null,
      wellbore_id: null,
      sequence_no: rows.length + 1,
      phase_entry_key: d.phase_entry_key,
      activity_code_entry_key: d.activity_code_entry_key,
      activity_subcode_entry_key: null,
      op_code_key: null,
      service_company_entry_key: null,
      time_from: body.time_from,
      time_to: body.time_to,
      duration_hours: body.duration_hours,
      // Entry-row md is typed in display units → convert to stored (feet-passthrough
      // on the demo, a real factor otherwise), matching the edit path.
      md_from: mdToStored(d.md_from),
      md_to: mdToStored(d.md_to),
      time_class: d.time_class,
      npt_level: isNpt ? "1" : "0",
      npt_class_entry_key: isNpt ? d.npt_class_key : null,
      npt_subclass_entry_key: null,
      npt_reason_key: null,
      activity_memo: d.activity_memo || null,
      change_reason: null,
    };
  };

  return (
    <section className="card" ref={rootRef}>
      <div className="panel-head-row">
        <h3>
          {t("time.section.log")}{" "}
          <span className="dim" style={{ fontWeight: 400, fontSize: 12 }}>
            — {t("time.section.log.note")}
          </span>
        </h3>
        {!readOnly && (
          <button type="button" className="ig-addbtn" onClick={focusAddRow}>
            <span className="ig-addbtn-plus" aria-hidden="true">
              +
            </span>
            {t("time.action.addRow")}
          </button>
        )}
      </div>

      <InlineRowGrid<ActivityOut, ActivityDraft>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        emptyDraft={emptyDraft}
        toRow={toRow}
        onCommit={(row) => {
          setAdded((a) => [...a, row]);
          void createActivity({
            reportId,
            body: {
              phase_entry_key: row.phase_entry_key,
              activity_code_entry_key: row.activity_code_entry_key,
              time_from: row.time_from,
              time_to: row.time_to,
              duration_hours: row.duration_hours,
              md_from: row.md_from,
              md_to: row.md_to,
              time_class: row.time_class,
              npt_level: row.npt_level,
              npt_class_entry_key: row.npt_class_entry_key,
              activity_memo: row.activity_memo,
            },
          });
        }}
        onDelete={(row) => {
          // Only server rows can be deleted; optimistic-local rows just drop.
          // Server delete keys off the STABLE entity_id, not the version-row id.
          if (row.entity_id.startsWith("act-local-")) {
            setAdded((a) => a.filter((r) => r.entity_id !== row.entity_id));
          } else {
            void deleteActivity({ entityId: row.entity_id, reportId });
          }
        }}
        rowOwner={() => "p"}
        readOnly={readOnly}
        ariaLabel={t("time.section.log")}
        emptyLabel={t("time.noActivities")}
        addLabel={t("time.action.addRow")}
      />

      {/* Running totals — sum of the entered segments (formatting-only). */}
      <div className="mud-total-row" data-owner="p">
        <span>{t("time.totalHours")}</span>
        <span className="mono tab-nums">
          {fmtNum(totalHours, locale, { maximumFractionDigits: 1 })} h
          {nptHours > 0 ? (
            <span className="dim">
              {" "}
              · {t("time.nptHours")} {fmtNum(nptHours, locale, { maximumFractionDigits: 1 })} h
            </span>
          ) : null}
        </span>
      </div>

      {/* Edit modal — reuses the same column inputs against the edit draft; saves
          via PATCH keyed off entity_id. Reuses columns[].input so the edit form
          stays in lockstep with the entry row. */}
      {editing ? (
        <Modal
          open
          title={t("time.action.editTitle")}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setEditing(null)}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={saveEdit}
                disabled={
                  !editDraft.activity_code_entry_key ||
                  !editDraft.time_from.trim() ||
                  !editDraft.time_to.trim()
                }
              >
                {t("common.save")}
              </button>
            </>
          }
        >
          <div className="time-edit-form">
            {columns
              .filter((c) => c.input && c.key !== "activity_memo") // memo → textarea below
              .map((c) => (
                <label key={c.key} className="time-edit-field">
                  <span className="kv-k">{c.header}</span>
                  {c.input!(editDraft, patchEdit)}
                </label>
              ))}
            {/* Description as a multi-line textarea — memos are sentences. */}
            <label className="time-edit-field">
              <span className="kv-k">{t("time.col.description")}</span>
              <textarea
                aria-label={t("time.col.description")}
                rows={3}
                value={editDraft.activity_memo}
                onChange={(e) => patchEdit({ activity_memo: e.target.value })}
                placeholder={t("time.col.description")}
              />
            </label>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
